import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageWrapper from "../../../components/PageWrapper";
import "./css/VehicleWeaponDetail.css";
import { LikeBar, ReactionBar, Bookmark, PresenceBadge, Discussion } from "../components";

/* ---------- pure helpers (NO hooks here) ---------- */
function toYouTubeEmbed(u = "") {
  if (!/youtu\.?be/.test(u)) return null;
  try {
    const normalized = u.replace("youtu.be/", "www.youtube.com/watch?v=");
    const url = new URL(normalized);
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    const last = url.pathname.split("/").filter(Boolean).pop();
    return last ? `https://www.youtube.com/embed/${last}` : null;
  } catch { return null; }
}
function Section({ id, title, children }) {
  const headingId = title ? `${id}-h` : undefined;
  return (
    <section id={id} className="vw-sec" {...(title ? { "aria-labelledby": headingId } : { "aria-label": id })}>
      {title && <h2 className="vw-h2" id={headingId}>{title}</h2>}
      {children}
    </section>
  );
}
function normalizeWeapon(d = {}) {
  const clean = (v) => (typeof v === "string" ? v.trim() : v);
  const tags = Array.isArray(d.tags) ? d.tags : d.tags ? String(d.tags).split(",").map((s)=>s.trim()).filter(Boolean) : [];
  const gallery = Array.isArray(d.gallery) ? d.gallery : [];
  const image = d.coverImage || (gallery.length ? gallery[0] : null) || d.image || d.imageUrl || null;
  return {
    name: d.name ?? "Unnamed Weapon",
    portrait: image,
    type: d.type ?? null,
    manufacturer: d.manufacturer ?? null,
    price: typeof d.price === "number" ? d.price : d.price ? Number(d.price) : null,
    dps: d.dps ?? null,
    fireRate: d.fireRate ?? null,
    reload: d.reload ?? null,
    ammoType: d.ammoType ?? null,
    description: clean(d.description) || null,
    acquisition: clean(d.acquisition) || null,
    attachments: clean(d.attachments) || null,
    gallery,
    videoUrl: d.videoUrl ?? null,
    tags,
  };
}

/* ---------- Component ---------- */
export default function WeaponDetail() {
  // 1) Hooks ALWAYS at the top level, before any early returns
  const { id } = useParams();
  const location = useLocation();

  const [w, setW] = useState(undefined);        // undefined=loading, null=404
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const lightboxRef = useRef(null);

  const basePath = useMemo(() => ["weapons", id], [id]);
  const bookmarkKey = useMemo(() => `weapons:${id}`, [id]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "weapons", id), (snap) => {
      if (!snap.exists()) return setW(null);
      const raw = snap.data();
      setW({ id: snap.id, ...normalizeWeapon(raw), _raw: raw });
    });
    return () => unsub();
  }, [id]);

  const yt = useMemo(
    () => (w?.videoUrl ? toYouTubeEmbed(w.videoUrl) : null),
    [w?.videoUrl]
  );

  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname + location.hash;
    try {
      if (navigator.share) { await navigator.share({ title: w?.name ?? "Weapon", url }); return; }
      await navigator.clipboard.writeText(url);
    } catch {}
  }, [w?.name, location.pathname, location.hash]);

  const openLightbox = useCallback((index) => setLightbox({ open: true, index }), []);
  const closeLightbox = useCallback(() => setLightbox({ open: false, index: 0 }), []);
  const nextLight = useCallback((e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = w?.gallery?.length ?? 0;
      return { open: true, index: len ? (s.index + 1) % len : 0 };
    });
  }, [w?.gallery?.length]);
  const prevLight = useCallback((e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = w?.gallery?.length ?? 0;
      return { open: true, index: len ? (s.index - 1 + len) % len : 0 };
    });
  }, [w?.gallery?.length]);

  useEffect(() => {
    if (!lightbox.open) return; // ✅ this guard is INSIDE the effect callback — fine
    function onKey(e){
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLight();
      if (e.key === "ArrowLeft") prevLight();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, nextLight, prevLight, closeLightbox]);

  // 2) Only AFTER all hooks: early returns for loading/404
  if (w === undefined) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-skel-card" aria-busy="true">
            <div className="vw-skel-hero" />
            <div className="vw-skel-title" />
            <div className="vw-skel-row" />
            <div className="vw-skel-section" />
          </div>
        </div>
      </PageWrapper>
    );
  }
  if (w === null) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-empty-card" role="alert">
            <div className="vw-empty-title">We can’t find that weapon</div>
            <p className="vw-empty-sub">It may have been removed or the link is incorrect.</p>
            <Link to="/community/vehicles_weapons?tab=weapons" className="vw-btn vw-btn--primary">← Back to Weapons</Link>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // 3) Normal render
  return (
    <PageWrapper>
      <div className="vw-wrap">
        <div className="vw-toprow">
          <Link to="/community/vehicles_weapons?tab=weapons" className="vw-back" aria-label="Back">← All weapons</Link>
          <div className="vw-actions">
            {w.videoUrl && !yt && <a className="vw-action" href={w.videoUrl} target="_blank" rel="noreferrer">▶ Open video</a>}
            <button className="vw-action" onClick={onShare}>⤴ Share</button>
          </div>
        </div>

        <header className="vw-hero-row">
          <div className="vw-hero-left">
            <div className="vw-portrait-wrap">
              {w.portrait ? (
                <img className="vw-portrait" src={w.portrait} alt={`${w.name} image`} loading="lazy" />
              ) : (
                <div className="vw-portrait vw-portrait--placeholder" aria-label="No image">🔫</div>
              )}
            </div>
          </div>

          <div className="vw-hero-right">
            <div className="vw-chips">
              {w.type && <span className="vw-chip">🧰 {w.type}</span>}
              {w.manufacturer && <span className="vw-chip">🏷 {w.manufacturer}</span>}
              {typeof w.price === "number" && <span className="vw-chip">💵 ${Number(w.price).toLocaleString("en-US")}</span>}
              {w.ammoType && <span className="vw-chip">🧨 {w.ammoType}</span>}
            </div>

            <h1 className="vw-title">{w.name}</h1>

            {w.description?.trim?.() && (
              <Section id="desc">
                <div className="vw-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{w.description}</ReactMarkdown>
                </div>
              </Section>
            )}

            <div className="vw-facts-grid">
              {w.dps != null && (<div className="vw-fact"><span>DPS</span><strong>{w.dps}</strong></div>)}
              {w.fireRate != null && (<div className="vw-fact"><span>Fire rate</span><strong>{w.fireRate}</strong></div>)}
              {w.reload != null && (<div className="vw-fact"><span>Reload</span><strong>{w.reload}s</strong></div>)}
            </div>
          </div>
        </header>

        <div className="vw-grid">
          <main className="vw-main">
            {(w.acquisition?.trim?.() || w.attachments?.trim?.()) && (
              <Section id="kit" title="Acquisition & Attachments">
                {w.acquisition?.trim?.() && (
                  <>
                    <h3 className="vw-h3">How to Get</h3>
                    <div className="vw-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{w.acquisition}</ReactMarkdown></div>
                  </>
                )}
                {w.attachments?.trim?.() && (
                  <>
                    <h3 className="vw-h3">Attachments</h3>
                    <div className="vw-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{w.attachments}</ReactMarkdown></div>
                  </>
                )}
              </Section>
            )}

            {Array.isArray(w.gallery) && w.gallery.length > 0 && (
              <Section id="gallery" title="Gallery">
                <div className="vw-gallery-grid">
                  {w.gallery.map((src, i) => (
                    <button key={i} className="vw-shotbtn" onClick={() => openLightbox(i)} aria-label={`Open image ${i + 1}`}>
                      <img className="vw-shot" src={src} alt={`Image ${i + 1}`} loading="lazy" />
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {w.videoUrl && (
              <Section id="video" title="Video">
                {yt ? (
                  <div className="vw-video">
                    <iframe
                      title="Weapon video"
                      src={yt}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <a className="vw-link" href={w.videoUrl} target="_blank" rel="noreferrer">▶ Open video</a>
                )}
              </Section>
            )}

            <div className="vw-engage">
              <PresenceBadge presenceKey="weapons" entityId={id} />
              <LikeBar basePath={basePath} />
              <ReactionBar basePath={basePath} />
              <Bookmark bookmarkKey={bookmarkKey} />
            </div>

            <Discussion basePath={basePath} />
          </main>
        </div>
      </div>

      {lightbox.open && Array.isArray(w.gallery) && w.gallery.length > 0 && (
        <div ref={lightboxRef} className="vw-lightbox" onClick={closeLightbox} role="dialog" aria-modal="true" aria-label="Image viewer">
          <button className="vw-lightbox-close" onClick={closeLightbox} aria-label="Close">✕</button>
          <button className="vw-lightbox-nav left" onClick={prevLight} aria-label="Previous">‹</button>
          <img className="vw-lightbox-img" src={w.gallery[lightbox.index]} alt={`Image ${lightbox.index + 1}`} />
          <button className="vw-lightbox-nav right" onClick={nextLight} aria-label="Next">›</button>
        </div>
      )}
    </PageWrapper>
  );
}
